import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import api, { formatApiError } from '../services/api';
import { getDateLocale } from '../i18n';
import Toast from './Toast';
import useToast from '../hooks/useToast';

const UsersPage = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [centreFilter, setCentreFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  const [users, setUsers] = useState([]);
  const [usersCount, setUsersCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  const [teams, setTeams] = useState([]);
  const [centres, setCentres] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingCentres, setLoadingCentres] = useState(false);
  const [teamsError, setTeamsError] = useState(null);
  const [centresError, setCentresError] = useState(null);

  // Form state for editing user
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    is_active: true,
    password: '',
    password_confirm: '',
    role: '',
    centre: '',
    team: ''
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, userId: null, userName: null });
  const [disablingMfaUserId, setDisablingMfaUserId] = useState(null);
  
  // State for add user modal
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addFormData, setAddFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    is_active: true,
    password: '',
    password_confirm: '',
    role: '',
    centre: '',
    team: ''
  });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addSuccess, setAddSuccess] = useState(false);
  const [showAddPassword, setShowAddPassword] = useState(false);
  const [showAddPasswordConfirm, setShowAddPasswordConfirm] = useState(false);
  const { toast, showSuccess, showError, showWarning, hideToast } = useToast();

  // Refs for status buttons animation
  const activeStatusRef = useRef(null);
  const inactiveStatusRef = useRef(null);
  const statusContainerRef = useRef(null);

  // Map API user data to display format
  const mapUserToDisplay = (user) => {
    const roleMap = {
      'ADMIN': { display: t('common:roleAdmin'), color: 'primary' },
      'MANAGER': { display: t('common:roleManager'), color: 'purple' },
      'EMPLOYE': { display: t('common:roleEmployee'), color: 'slate' }
    };
    
    const roleInfo = roleMap[user.role] || { display: user.role, color: 'slate' };
    
    return {
      id: user.id,
      name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
      email: user.email,
      role: roleInfo.display,
      roleColor: roleInfo.color,
      status: user.is_active ? t('common:active') : t('common:inactive'),
      statusColor: user.is_active ? 'emerald' : 'red',
      registrationDate: user.date_joined ? new Date(user.date_joined).toLocaleDateString(getDateLocale()) : t('common:na'),
      // For employees: last operation date (most recent imputation). For others: —
      lastOperationDate: user.role === 'EMPLOYE' && user.last_operation_date
        ? new Date(user.last_operation_date).toLocaleDateString(getDateLocale())
        : (user.role === 'EMPLOYE' ? t('users.never') : '—'),
      avatar: user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.first_name || user.email)}&background=random`,
      mfa_enabled: user.mfa_enabled === true,
      rawData: user // Keep original data for detail panel
    };
  };

  const selectedUserData = users.find(u => u.id === selectedUser);

  // Initialize form data when user is selected
  useEffect(() => {
    if (selectedUserData?.rawData) {
      const userData = selectedUserData.rawData;
      const isAdmin = userData.role === 'ADMIN';
      setEditFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.email || '',
        is_active: userData.is_active !== undefined ? userData.is_active : true,
        password: '',
        password_confirm: '',
        role: userData.role || '',
        centre: (userData.centre_id ?? userData.centre?.id)?.toString() || '',
        team: isAdmin ? '' : (userData.team?.id?.toString() || '')
      });
      setSaveError(null);
      setSaveSuccess(false);
      // Reset password visibility when switching users
      setShowPassword(false);
      setShowPasswordConfirm(false);
    }
  }, [selectedUserData]);

  // Fetch users from backend
  const fetchUsers = async () => {
    setLoadingUsers(true);
    setUsersError(null);
    try {
      const filters = {};
      if (roleFilter) filters.role = roleFilter;
      if (statusFilter) filters.status = statusFilter;
      if (searchQuery) filters.search = searchQuery;
      if (centreFilter) filters.centre = centreFilter;
      if (teamFilter) filters.team = teamFilter;

      const response = await api.getUsers(filters, currentPage, pageSize);
      const mappedUsers = (response.results || []).map(mapUserToDisplay);
      setUsers(mappedUsers);
      setUsersCount(response.count || 0);
      
      // Clear selection if selected user is not in current page results
      if (selectedUser && !mappedUsers.find(u => u.id === selectedUser)) {
        setSelectedUser(null);
      }
    } catch (error) {
      const formattedError = formatApiError(error);
      setUsersError(formattedError.generalError || t('users.loadFailed'));
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch teams and centres from backend
  useEffect(() => {
    const fetchTeams = async () => {
      setLoadingTeams(true);
      setTeamsError(null);
      try {
        const response = await api.getTeamsFilter();
        const list = Array.isArray(response) ? response : (response?.results ?? []);
        setTeams(list);
      } catch (error) {
        const formattedError = formatApiError(error);
        setTeamsError(formattedError.generalError || t('teams.loadFailed'));
        console.error('Error fetching teams:', error);
      } finally {
        setLoadingTeams(false);
      }
    };

    const fetchCentres = async () => {
      setLoadingCentres(true);
      setCentresError(null);
      try {
        const response = await api.getCentresFilter();
        setCentres(Array.isArray(response) ? response : []);
      } catch (error) {
        const formattedError = formatApiError(error);
        setCentresError(formattedError.generalError || t('centres.loadFailed'));
        console.error('Error fetching centres:', error);
      } finally {
        setLoadingCentres(false);
      }
    };

    fetchTeams();
    fetchCentres();
  }, []);

  // Reset to page 1 when filters change (except page itself)
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, statusFilter, searchQuery, centreFilter, teamFilter]);

  // Fetch users when filters, page, or page size changes
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, roleFilter, statusFilter, searchQuery, centreFilter, teamFilter]);

  // Handle Escape key to close modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (selectedUser) {
          setSelectedUser(null);
        }
        if (showAddUserModal) {
          setShowAddUserModal(false);
        }
      }
    };

    if (selectedUser || showAddUserModal) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [selectedUser, showAddUserModal]);

  // Teams are no longer scoped by centre; show all teams in filter
  const filteredTeams = teams;

  const handleClearFilters = () => {
    setSearchQuery('');
    setRoleFilter('');
    setTeamFilter('');
    setCentreFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFormChange = (field, value) => {
    setEditFormData(prev => {
      const next = { ...prev, [field]: value };
      // Admin should not have a team - clear team when role changes to ADMIN
      if (field === 'role' && value === 'ADMIN') {
        next.team = '';
      }
      return next;
    });
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleAddFormChange = (field, value) => {
    setAddFormData(prev => {
      const next = { ...prev, [field]: value };
      // Admin should not have a team - clear team when role changes to ADMIN
      if (field === 'role' && value === 'ADMIN') {
        next.team = '';
      }
      return next;
    });
    setAddError(null);
    setAddSuccess(false);
  };

  const handleOpenAddModal = () => {
    setAddFormData({
      first_name: '',
      last_name: '',
      email: '',
      is_active: true,
      password: '',
      password_confirm: '',
      role: '',
      centre: '',
      team: ''
    });
    setAddError(null);
    setAddSuccess(false);
    setShowAddPassword(false);
    setShowAddPasswordConfirm(false);
    setShowAddUserModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddUserModal(false);
    setAddError(null);
    setAddSuccess(false);
  };

  const handleAddUser = async () => {
    // Validate required fields
    if (!addFormData.email) {
      setAddError(t('users.emailRequired'));
      return;
    }
    if (!addFormData.first_name) {
      setAddError(t('users.firstNameRequired'));
      return;
    }
    if (!addFormData.last_name) {
      setAddError(t('users.lastNameRequired'));
      return;
    }
    if (!addFormData.role) {
      setAddError(t('users.roleRequired'));
      return;
    }

    // Validate centre for employees only
    if (addFormData.role === 'EMPLOYE' && !addFormData.centre) {
      setAddError(t('users.centreRequiredForEmployees'));
      return;
    }

    setAdding(true);
    setAddError(null);
    setAddSuccess(false);

    try {
      // Step 1: Create user via register API (creates user as active by default)
      const userData = {
        email: addFormData.email,
        first_name: addFormData.first_name,
        last_name: addFormData.last_name,
        role: addFormData.role,
        centre_id: addFormData.role === 'EMPLOYE' && addFormData.centre ? parseInt(addFormData.centre) : null,
        team: addFormData.role !== 'ADMIN' && addFormData.team ? parseInt(addFormData.team) : null,
      };

      const createdUser = await api.createUser(userData);
      
      // Step 2: Update is_active status if needed
      // The register API creates users as active by default, but we update to ensure correct status
      if (createdUser && createdUser.id) {
        if (!addFormData.is_active) {
          await api.updateUser(createdUser.id, {
            is_active: addFormData.is_active
          });
        }
      } else {
        throw new Error(t('users.userCreatedButNoId'));
      }
      
      // Refresh users list
      await fetchUsers();
      
      // Close modal and reset form
      setShowAddUserModal(false);
      setAddFormData({
        first_name: '',
        last_name: '',
        email: '',
        is_active: true,
        password: '',
        password_confirm: '',
        role: '',
        centre: '',
        team: ''
      });
      setAddSuccess(true);
    } catch (error) {
      const formattedError = formatApiError(error);
      setAddError(formattedError.generalError || t('users.createFailed'));
      console.error('Error creating user:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    // Validate passwords if provided
    if (editFormData.password || editFormData.password_confirm) {
      if (editFormData.password !== editFormData.password_confirm) {
        setSaveError(t('users.passwordsDoNotMatch'));
        return;
      }
      if (editFormData.password.length < 8) {
        setSaveError(t('users.passwordMinLength'));
        return;
      }
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Prepare update data
      const updateData = {
        first_name: editFormData.first_name,
        last_name: editFormData.last_name,
        email: editFormData.email,
        is_active: editFormData.is_active,
        role: editFormData.role,
      };

      // Only include password if provided
      if (editFormData.password) {
        updateData.password = editFormData.password;
      }

      // Centre (required for employees only) and team (optional for non-admin roles only)
      if (editFormData.role === 'EMPLOYE') {
        updateData.centre_id = editFormData.centre ? parseInt(editFormData.centre) : null;
      } else {
        updateData.centre_id = null;
      }
      // Admin should not have a team
      if (editFormData.role === 'ADMIN') {
        updateData.team_id = null;
        updateData.team = null;
      } else if (editFormData.team) {
        const teamId = parseInt(editFormData.team);
        updateData.team_id = teamId;
        updateData.team = teamId;
      } else {
        updateData.team_id = null;
        updateData.team = null;
      }

      const response = await api.updateUser(selectedUser, updateData);
      
      // Update the user in the local state with the response data
      setUsers(prevUsers => 
        prevUsers.map(user => {
          if (user.id === selectedUser) {
            // Use response data if available, otherwise merge with existing data
            const updatedRawData = response || {
              ...user.rawData,
              ...updateData,
              centre: updateData.centre_id != null ? (centres.find(c => c.id === updateData.centre_id) || user.rawData.centre) : null,
              team: updateData.team !== null && updateData.team !== undefined 
                ? teams.find(t => t.id === updateData.team) || user.rawData.team
                : null
            };
            return mapUserToDisplay(updatedRawData);
          }
          return user;
        })
      );
      
      // Close the selected user panel
      setSelectedUser(null);
      
      // Clear password fields after successful save
      setEditFormData(prev => ({
        ...prev,
        password: '',
        password_confirm: ''
      }));
    } catch (error) {
      const formattedError = formatApiError(error);
      setSaveError(formattedError.generalError || t('users.saveFailed'));
      console.error('Error updating user:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus, e) => {
    e.stopPropagation();
    const newStatus = !currentStatus;
    
    // Optimistically update the UI
    setUsers(prevUsers => 
      prevUsers.map(user => {
        if (user.id === userId) {
          const updatedRawData = { ...user.rawData, is_active: newStatus };
          return {
            ...user,
            status: newStatus ? t('common:active') : t('common:inactive'),
            statusColor: newStatus ? 'emerald' : 'red',
            rawData: updatedRawData
          };
        }
        return user;
      })
    );

    try {
      await api.updateUser(userId, { is_active: newStatus });
    } catch (error) {
      // Revert on error
      setUsers(prevUsers => 
        prevUsers.map(user => {
          if (user.id === userId) {
            const updatedRawData = { ...user.rawData, is_active: currentStatus };
            return {
              ...user,
              status: currentStatus ? t('common:active') : t('common:inactive'),
              statusColor: currentStatus ? 'emerald' : 'red',
              rawData: updatedRawData
            };
          }
          return user;
        })
      );
      const formattedError = formatApiError(error);
      alert(formattedError.generalError || t('users.statusUpdateFailed'));
      console.error('Error toggling user status:', error);
    }
  };

  const handleDeleteClick = (userId, userName, e) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, userId, userName });
  };

  const handleDisableMfaForUser = async (userId, userName, e) => {
    e.stopPropagation();
    if (!window.confirm(t('users.disableMfaConfirm', { name: userName }))) {
      return;
    }
    setDisablingMfaUserId(userId);
    try {
      await api.disableMfaForUser(userId);
      showSuccess(t('users.mfaDisabled'), t('users.mfaDisabledMessage', { name: userName }));
      fetchUsers();
    } catch (error) {
      const formattedError = formatApiError(error);
      showError(t('users.mfaDisableFailed'), formattedError.generalError || t('users.mfaDisableFailedMessage'));
    } finally {
      setDisablingMfaUserId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    const { userId, userName } = deleteModal;
    if (!userId) return;

    // Optimistically remove from UI
    const userToDelete = users.find(u => u.id === userId);
    setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
    setUsersCount(prevCount => Math.max(0, prevCount - 1));
    
    // Clear selection if deleted user was selected
    if (selectedUser === userId) {
      setSelectedUser(null);
    }

    // Close modal
    setDeleteModal({ isOpen: false, userId: null, userName: null });

    try {
      await api.deleteUser(userId);
      // Show success toast
      showSuccess(t('users.deletedTitle'), t('users.deletedMessage', { name: userName }));
    } catch (error) {
      // Revert on error - add user back
      if (userToDelete) {
        setUsers(prevUsers => [...prevUsers, userToDelete].sort((a, b) => a.id - b.id));
        setUsersCount(prevCount => prevCount + 1);
      }
      const formattedError = formatApiError(error);
      // Show error toast
      showError(t('users.deleteFailed'), formattedError.generalError || t('users.deleteFailedMessage'));
      
      // Show API warnings if present
      if (error.response?.status === 403) {
        showWarning(t('users.permissionDenied'), t('users.permissionDeniedMessage'), 5000);
      } else if (error.response?.status === 409) {
        showWarning(t('users.cannotDelete'), t('users.cannotDeleteMessage'), 5000);
      }
      console.error('Error deleting user:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, userId: null, userName: null });
  };

  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, usersCount);
  const totalPages = Math.ceil(usersCount / pageSize);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const getRoleBadgeClass = (roleColor) => {
    const colors = {
      primary: 'bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:text-primary dark:border-primary/40',
      purple: 'bg-purple-50 text-purple-700 border-purple-100 dark:bg-purple-900/40 dark:text-purple-200 dark:border-purple-700/70',
      slate: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-700/60 dark:text-slate-200 dark:border-slate-600/80'
    };
    return colors[roleColor] || colors.slate;
  };

  const getStatusBadgeClass = (statusColor) => {
    const colors = {
      emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700/70',
      amber: 'bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700/70',
      red: 'bg-red-50 text-red-700 border-red-100 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700/70'
    };
    return colors[statusColor] || colors.emerald;
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedUsers = () => {
    if (!sortColumn) return users;

    return [...users].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'name':
          // Sort by first name, then last name
          const aFirstName = a.rawData?.first_name || '';
          const aLastName = a.rawData?.last_name || '';
          const bFirstName = b.rawData?.first_name || '';
          const bLastName = b.rawData?.last_name || '';
          aValue = `${aFirstName} ${aLastName}`.trim().toLowerCase();
          bValue = `${bFirstName} ${bLastName}`.trim().toLowerCase();
          break;
        case 'registrationDate':
          aValue = a.rawData?.date_joined ? new Date(a.rawData.date_joined).getTime() : 0;
          bValue = b.rawData?.date_joined ? new Date(b.rawData.date_joined).getTime() : 0;
          break;
        case 'lastOperationDate':
          aValue = a.rawData?.last_operation_date ? new Date(a.rawData.last_operation_date).getTime() : 0;
          bValue = b.rawData?.last_operation_date ? new Date(b.rawData.last_operation_date).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableHeader = ({ column, children }) => {
    const isActive = sortColumn === column;
    return (
      <th 
        className="px-6 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors select-none"
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-2">
          <span>{children}</span>
          <div className="flex flex-col">
            <span className={`material-symbols-outlined text-[14px] leading-none ${
              isActive && sortDirection === 'asc' ? 'text-primary' : 'text-slate-300'
            }`}>
              arrow_drop_up
            </span>
            <span className={`material-symbols-outlined text-[14px] leading-none -mt-1 ${
              isActive && sortDirection === 'desc' ? 'text-primary' : 'text-slate-300'
            }`}>
              arrow_drop_down
            </span>
          </div>
        </div>
      </th>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Heading & Actions */}
      <div className="flex flex-wrap justify-between items-end gap-4 px-1">
        <div className="flex min-w-72 flex-col gap-2">
          <h1 className="text-slate-900 text-3xl font-bold leading-tight tracking-[-0.02em]">
            {t('users.title')}
          </h1>
          <p className="text-slate-500 text-base font-normal leading-normal">
            {t('users.subtitle')}
          </p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={handleOpenAddModal}
            className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold shadow-md hover:bg-primary-hover transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">add</span>
            <span className="truncate">{t('users.addNewUser')}</span>
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col min-w-64 flex-1">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider pb-2">
              {t('users.searchLabel')}
            </p>
            <div className="relative">
              <input
                className="form-input w-full rounded-lg text-slate-900 dark:text-slate-100 focus:outline-0 focus:ring-2 focus:ring-primary/50 border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 h-10 placeholder:text-slate-400 dark:placeholder-slate-400 pl-10 pr-4 text-sm"
                placeholder={t('users.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-[20px]">search</span>
            </div>
          </label>
          <div className="flex gap-3 flex-wrap items-end">
            <div className="flex flex-col gap-2">
              <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('users.role')}</p>
              <div className="relative">
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="h-10 appearance-none rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-4 pr-10 min-w-[140px] text-slate-700 dark:text-slate-100 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
                >
                  <option value="">{t('users.filterAllRoles')}</option>
                  <option value="EMPLOYE">{t('common:roleEmployee')}</option>
                  <option value="MANAGER">{t('common:roleManager')}</option>
                  <option value="ADMIN">{t('common:roleAdmin')}</option>
                </select>
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('users.centre')}</p>
              <div className="relative">
                <select
                  value={centreFilter}
                  onChange={(e) => setCentreFilter(e.target.value)}
                  disabled={loadingCentres}
                  className="h-10 appearance-none rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-4 pr-10 min-w-[140px] text-slate-700 dark:text-slate-100 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">{t('users.filterAllCentres')}</option>
                  {centres.map((centre) => (
                    <option key={centre.id} value={centre.id}>
                      {centre.name}
                    </option>
                  ))}
                </select>
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('users.team')}</p>
              <div className="relative">
                <select
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.target.value)}
                  disabled={loadingTeams}
                  className="h-10 appearance-none rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-4 pr-10 min-w-[140px] text-slate-700 dark:text-slate-100 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">{t('users.filterAllTeams')}</option>
                  {filteredTeams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('users.status')}</p>
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-10 appearance-none rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-4 pr-10 min-w-[140px] text-slate-700 dark:text-slate-100 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
                >
                  <option value="">{t('users.filterAllStatuses')}</option>
                  <option value="active">{t('common:active')}</option>
                  <option value="inactive">{t('common:inactive')}</option>
                </select>
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
            </div>
            <button
              onClick={handleClearFilters}
              className="h-10 px-4 text-sm font-medium text-primary hover:text-blue-700 transition-colors ml-2"
            >
              {t('common:clearAllFilters')}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 items-start">
        {/* User Table */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{t('users.managementTitle')}</h3>
                {!roleFilter && (() => {
                  const adminsOnPage = users.filter(u => u.rawData?.role === 'ADMIN').length;
                  return adminsOnPage > 0 ? (
                    <p className="text-xs text-slate-500 mt-0.5">
                      {t('users.includingAdmins', { count: adminsOnPage })}
                    </p>
                  ) : null;
                })()}
              </div>
              <span className="text-xs font-medium bg-slate-100 text-slate-600 px-2 py-1 rounded dark:bg-slate-700 dark:text-slate-300">
                {t('users.count', { count: usersCount })}
              </span>
            </div>
            <div className="overflow-x-auto">
            {usersError ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-red-500">{usersError}</div>
              </div>
            ) : users.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-slate-500">{t('users.noUsers')}</div>
              </div>
            ) : (
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                  <tr>
                    <SortableHeader column="name">{t('common:user')}</SortableHeader>
                    <th className="px-6 py-3 font-semibold">{t('users.role')}</th>
                    <th className="px-6 py-3 font-semibold">{t('users.status')}</th>
                    <SortableHeader column="registrationDate">{t('users.registrationDate')}</SortableHeader>
                    <SortableHeader column="lastOperationDate">{t('users.lastOperation')}</SortableHeader>
                    <th className="px-6 py-3 font-semibold text-right">{t('users.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {getSortedUsers().map((user) => (
                  <motion.tr
                    key={user.id}
                    className={`hover:bg-slate-50 transition-colors group cursor-pointer border-l-4 ${
                      selectedUser === user.id
                        ? 'bg-primary/5'
                        : 'border-l-transparent'
                    }`}
                    onClick={() => {
                      setSelectedUser(selectedUser === user.id ? null : user.id);
                    }}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="h-9 w-9 rounded-full bg-cover bg-center"
                          data-alt={t('users.portraitOf', { name: user.name })}
                          style={{ backgroundImage: `url('${user.avatar}')` }}
                        ></div>
                        <div>
                          <p className="font-semibold text-slate-900">{user.name}</p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${getRoleBadgeClass(user.roleColor)}`}>
                        <span className="material-symbols-outlined text-[14px]">
                          {user.rawData?.role === 'ADMIN' ? 'shield_person' : user.rawData?.role === 'MANAGER' ? 'manage_accounts' : 'badge'}
                        </span>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(user.statusColor)}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${
                          user.statusColor === 'emerald' ? 'bg-emerald-500' :
                          user.statusColor === 'amber' ? 'bg-amber-500' :
                          'bg-red-500'
                        }`}></span>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-500">{user.registrationDate}</td>
                    <td className="px-6 py-4 text-slate-500">{user.lastOperationDate}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2.5">
                        {/* Disable MFA Button - only when user has MFA enabled */}
                        {(user.mfa_enabled || user.rawData?.mfa_enabled) && (
                          <button
                            onClick={(e) => handleDisableMfaForUser(user.id, user.name, e)}
                            disabled={disablingMfaUserId === user.id}
                            className="h-9 px-4 rounded-lg text-xs font-semibold flex items-center gap-2 shadow-sm bg-gradient-to-r from-amber-50 to-amber-100/50 text-amber-700 border border-amber-300/50 hover:from-amber-100 hover:to-amber-200/50 dark:from-amber-900/40 dark:to-amber-800/30 dark:text-amber-200 dark:border-amber-700/70 dark:hover:from-amber-800/50 dark:hover:to-amber-700/40 disabled:opacity-50"
                            title={t('users.disableMfaTitle')}
                          >
                            <span className="material-symbols-outlined text-[18px]">
                              lock_open
                            </span>
                            <span className="font-medium">{disablingMfaUserId === user.id ? t('users.disablingMfa') : t('users.disableMfa')}</span>
                          </button>
                        )}
                        {/* Toggle Active/Inactive Button */}
                        <button
                          onClick={(e) => handleToggleUserStatus(user.id, user.rawData?.is_active, e)}
                          className={`h-9 px-4 rounded-lg text-xs font-semibold flex items-center gap-2 shadow-sm ${
                            user.rawData?.is_active
                              ? 'bg-gradient-to-r from-emerald-50 to-emerald-100/50 text-emerald-700 border border-emerald-300/50 dark:from-emerald-900/40 dark:to-emerald-800/30 dark:text-emerald-200 dark:border-emerald-700/70'
                              : 'bg-gradient-to-r from-slate-100 to-slate-50 text-slate-600 border border-slate-300/50 dark:from-slate-700 dark:to-slate-800 dark:text-slate-300 dark:border-slate-600'
                          }`}
                          title={user.rawData?.is_active ? t('users.deactivate') : t('users.activate')}
                        >
                          <span 
                            className={`material-symbols-outlined text-[18px] ${
                              user.rawData?.is_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'
                            }`}
                          >
                            {user.rawData?.is_active ? 'check_circle' : 'cancel'}
                          </span>
                          <span className="font-medium">{user.rawData?.is_active ? t('common:active') : t('common:inactive')}</span>
                        </button>
                        {/* Delete Button */}
                        <button
                          onClick={(e) => handleDeleteClick(user.id, user.name, e)}
                          className="h-9 px-4 rounded-lg text-xs font-semibold flex items-center gap-2 shadow-sm bg-gradient-to-r from-red-50 to-red-100/50 text-red-600 border border-red-300/50 dark:from-red-900/30 dark:to-red-800/20 dark:text-red-200 dark:border-red-700/70"
                          title={t('users.deleteUser')}
                        >
                          <span className="material-symbols-outlined text-[18px]">
                            delete_outline
                          </span>
                          <span className="font-medium">{t('common:delete')}</span>
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                  ))}
                </tbody>
              </table>
            )}
            </div>
            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-500">
                  {t('common:showingRange', { from: usersCount > 0 ? startIndex : 0, to: endIndex, total: usersCount })}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">{t('common:perPage')}</span>
                  <div className="relative">
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="appearance-none rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-1.5 pr-8 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-[16px] pointer-events-none">arrow_drop_down</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:firstPage')}
                >
                  <span className="material-symbols-outlined text-sm">first_page</span>
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:previousPage')}
                >
                  <span className="material-symbols-outlined text-sm">chevron_left</span>
                </button>
                
                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-slate-400">...</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`h-8 w-8 flex items-center justify-center rounded border text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-primary text-white border-primary'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:nextPage')}
                >
                  <span className="material-symbols-outlined text-sm">chevron_right</span>
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage >= totalPages}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:lastPage')}
                >
                  <span className="material-symbols-outlined text-sm">last_page</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Edit Modal */}
      <AnimatePresence>
        {selectedUser && selectedUserData && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setSelectedUser(null)}
            />
            {/* Modal */}
            <div  onClick={() => setSelectedUser(null)} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full border border-slate-200 my-8 max-h-[90vh] overflow-y-auto"
                onClick={(e) => {
                  // Close modal if clicking directly on the modal container (empty/padding areas)
                  if (e.target === e.currentTarget) {
                    setSelectedUser(null);
                  }
                }}
              >
                <div 
                  className="min-h-full"
                  onClick={(e) => e.stopPropagation()}
                >
                <div className="p-6 border-b border-slate-100 flex justify-between items-start sticky top-0 bg-white z-10 relative">
                  <div className="flex-1 pr-12">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('users.selectedUser')}</p>
                    <h3 className="text-xl font-bold text-slate-900">{selectedUserData.name}</h3>
                    <p className="text-sm text-slate-500">
                      {selectedUserData.rawData?.role === 'ADMIN'
                        ? t('users.adminNoCentreOrTeam')
                        : `${selectedUserData.rawData?.centre?.name ?? selectedUserData.rawData?.centre_name ?? t('users.noCentreAssigned')}${selectedUserData.rawData?.team?.name ? ` · ${selectedUserData.rawData.team.name}` : ''}`}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="absolute top-6 right-6 h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-1"
                    title={t('users.closeModal')}
                  >
                    <span className="material-symbols-outlined text-[22px] group-hover:scale-110 transition-transform duration-200">close</span>
                  </button>
                </div>
                <div className="p-6 space-y-6">
              {/* Success/Error Messages */}
              {saveSuccess && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200 text-sm">
                  {t('users.updated')}
                </div>
              )}
              {saveError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 text-sm">
                  {saveError}
                </div>
              )}

              {/* User Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('users.firstName')}</label>
                  <input
                    type="text"
                    value={editFormData.first_name}
                    onChange={(e) => handleFormChange('first_name', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none placeholder-slate-500 dark:placeholder-slate-400"
                    placeholder={t('users.firstName')}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('users.lastName')}</label>
                  <input
                    type="text"
                    value={editFormData.last_name}
                    onChange={(e) => handleFormChange('last_name', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none placeholder-slate-500 dark:placeholder-slate-400"
                    placeholder={t('users.lastName')}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('users.email')}</label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none placeholder-slate-500 dark:placeholder-slate-400"
                    placeholder={t('users.emailPlaceholder')}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('users.status')}</label>
                  <div ref={statusContainerRef} className="relative flex h-12 w-full items-center justify-center rounded-lg bg-slate-50 p-1 border border-slate-200">
                    {/* Animated sliding background */}
                    <motion.div
                      className="absolute h-[calc(100%-0.5rem)] rounded-md bg-white shadow-sm"
                      initial={false}
                      animate={{
                        x: editFormData.is_active ? 0 : '100%',
                        width: 'calc(50% - 0.25rem)'
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30
                      }}
                      style={{
                        left: '0.125rem'
                      }}
                    />
                    <label className="flex-1 cursor-pointer h-full relative z-10">
                      <input
                        type="radio"
                        name="is_active"
                        checked={editFormData.is_active === true}
                        onChange={() => handleFormChange('is_active', true)}
                        className="sr-only"
                      />
                      <motion.div
                        ref={activeStatusRef}
                        className="flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10"
                        animate={{
                          color: editFormData.is_active ? '#2563eb' : '#64748b'
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {t('common:active')}
                      </motion.div>
                    </label>
                    <label className="flex-1 cursor-pointer h-full relative z-10">
                      <input
                        type="radio"
                        name="is_active"
                        checked={editFormData.is_active === false}
                        onChange={() => handleFormChange('is_active', false)}
                        className="sr-only"
                      />
                      <motion.div
                        ref={inactiveStatusRef}
                        className="flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10"
                        animate={{
                          color: editFormData.is_active === false ? '#2563eb' : '#64748b'
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {t('common:inactive')}
                      </motion.div>
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('common:password')}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={editFormData.password}
                      onChange={(e) => handleFormChange('password', e.target.value)}
                      className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 pr-10 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none placeholder-slate-500 dark:placeholder-slate-400"
                      placeholder={t('users.passwordPlaceholder')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      title={showPassword ? t('common:hidePassword') : t('common:showPassword')}
                    >
                      <span className="material-symbols-outlined text-[20px]">
                        {showPassword ? "visibility_off" : "visibility"}
                      </span>
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('users.confirmPassword')}</label>
                  <div className="relative">
                    <input
                      type={showPasswordConfirm ? "text" : "password"}
                      value={editFormData.password_confirm}
                      onChange={(e) => handleFormChange('password_confirm', e.target.value)}
                      className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 pr-10 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none placeholder-slate-500 dark:placeholder-slate-400"
                      placeholder={t('users.confirmPasswordPlaceholder')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      title={showPasswordConfirm ? t('common:hidePassword') : t('common:showPassword')}
                    >
                      <span className="material-symbols-outlined text-[20px]">
                        {showPasswordConfirm ? "visibility_off" : "visibility"}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Select Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('users.role')}</label>
                  <div className="relative">
                    <select
                      value={editFormData.role}
                      onChange={(e) => handleFormChange('role', e.target.value)}
                      className="w-full appearance-none rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 pr-10 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    >
                      <option value="">{t('users.selectRole')}</option>
                      <option value="EMPLOYE">{t('common:roleEmployee')}</option>
                      <option value="MANAGER">{t('common:roleManager')}</option>
                      <option value="ADMIN">{t('common:roleAdmin')}</option>
                    </select>
                    <span className="material-symbols-outlined absolute right-3 top-3 text-slate-500 pointer-events-none">arrow_drop_down</span>
                  </div>
                </div>

                {/* Centre field - required for Employee */}
                {editFormData.role === 'EMPLOYE' && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-900 block">{t('users.centre')} *</label>
                    <div className="relative">
                      <select
                        value={editFormData.centre}
                        onChange={(e) => handleFormChange('centre', e.target.value)}
                        className="w-full appearance-none rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 pr-10 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        disabled={loadingCentres}
                      >
                        <option value="">{t('users.selectCentre')}</option>
                        {centres.map((c) => (
                          <option key={c.id} value={c.id.toString()}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <span className="material-symbols-outlined absolute right-3 top-3 text-slate-500 pointer-events-none">arrow_drop_down</span>
                    </div>
                  </div>
                )}

                {/* Team field - show for non-admin roles only (admins should not have a team) */}
                {editFormData.role !== 'ADMIN' && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-900 block">{t('users.team')}</label>
                    <div className="relative">
                      <select
                        value={editFormData.team}
                        onChange={(e) => handleFormChange('team', e.target.value)}
                        className="w-full appearance-none rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 pr-10 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        disabled={loadingTeams}
                      >
                        <option value="">{t('users.noTeam')}</option>
                        {teams.map((team) => (
                          <option key={team.id} value={team.id.toString()}>
                            {team.name}
                          </option>
                        ))}
                      </select>
                      <span className="material-symbols-outlined absolute right-3 top-3 text-slate-500 pointer-events-none">arrow_drop_down</span>
                    </div>
                  </div>
                )}

                {/* Managed Teams - read-only for Managers */}
                {editFormData.role === 'MANAGER' && (
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-semibold text-slate-900 block">{t('users.managedTeams')}</label>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 min-h-[60px]">
                      {selectedUserData?.rawData?.managed_teams && selectedUserData.rawData.managed_teams.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {selectedUserData.rawData.managed_teams.map((team) => (
                            <span
                              key={team.id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white border border-slate-200 text-slate-700"
                            >
                              <span className="material-symbols-outlined text-[16px] text-slate-500">group</span>
                              {team.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 italic">{t('users.noTeamsAssigned')}</p>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">{t('users.managedTeamsHintEdit')}</p>
                  </div>
                )}
              </div>

              {/* Save Button */}
              <div className="pt-4 border-t border-slate-100">
                <button
                  onClick={handleSaveUser}
                  disabled={saving}
                  className="w-full h-10 rounded-lg bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? t('common:saving') : t('users.saveChanges')}
                </button>
              </div>
                </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Add User Modal */}
      <AnimatePresence>
        {showAddUserModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={handleCloseAddModal}
            />
            {/* Modal */}
            <div onClick={handleCloseAddModal} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full border border-slate-200 my-8 max-h-[90vh] overflow-y-auto"
                onClick={(e) => {
                  // Close modal if clicking directly on the modal container (empty/padding areas)
                  if (e.target === e.currentTarget) {
                    handleCloseAddModal();
                  }
                }}
              >
                <div 
                  className="min-h-full"
                  onClick={(e) => e.stopPropagation()}
                >
                <div className="p-6 border-b border-slate-100 flex justify-between items-start sticky top-0 bg-white z-10 relative">
                  <div className="flex-1 pr-12">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('users.addNewUser')}</p>
                    <h3 className="text-xl font-bold text-slate-900">{t('users.createUser')}</h3>
                    <p className="text-sm text-slate-500">
                      {t('users.createSubtitle')}
                    </p>
                  </div>
                  <button
                    onClick={handleCloseAddModal}
                    className="absolute top-6 right-6 h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-1"
                    title={t('users.closeModal')}
                  >
                    <span className="material-symbols-outlined text-[22px] group-hover:scale-110 transition-transform duration-200">close</span>
                  </button>
                </div>
                <div className="p-6 space-y-6">
              {/* Success/Error Messages */}
              {addSuccess && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200 text-sm">
                  {t('users.created')}
                </div>
              )}
              {addError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 text-sm">
                  {addError}
                </div>
              )}

              {/* User Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('users.firstName')} *</label>
                  <input
                    type="text"
                    value={addFormData.first_name}
                    onChange={(e) => handleAddFormChange('first_name', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none placeholder-slate-500 dark:placeholder-slate-400"
                    placeholder={t('users.firstName')}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('users.lastName')} *</label>
                  <input
                    type="text"
                    value={addFormData.last_name}
                    onChange={(e) => handleAddFormChange('last_name', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none placeholder-slate-500 dark:placeholder-slate-400"
                    placeholder={t('users.lastName')}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('users.email')} *</label>
                  <input
                    type="email"
                    value={addFormData.email}
                    onChange={(e) => handleAddFormChange('email', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none placeholder-slate-500 dark:placeholder-slate-400"
                    placeholder={t('users.emailPlaceholder')}
                    autoComplete="off"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('users.status')}</label>
                  <div ref={statusContainerRef} className="relative flex h-12 w-full items-center justify-center rounded-lg bg-slate-50 p-1 border border-slate-200">
                    {/* Animated sliding background */}
                    <motion.div
                      className="absolute h-[calc(100%-0.5rem)] rounded-md bg-white shadow-sm"
                      initial={false}
                      animate={{
                        x: addFormData.is_active ? 0 : '100%',
                        width: 'calc(50% - 0.25rem)'
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30
                      }}
                      style={{
                        left: '0.125rem'
                      }}
                    />
                    <label className="flex-1 cursor-pointer h-full relative z-10">
                      <input
                        type="radio"
                        name="is_active_add"
                        checked={addFormData.is_active === true}
                        onChange={() => handleAddFormChange('is_active', true)}
                        className="sr-only"
                      />
                      <motion.div
                        ref={activeStatusRef}
                        className="flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10"
                        animate={{
                          color: addFormData.is_active ? '#2563eb' : '#64748b'
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {t('common:active')}
                      </motion.div>
                    </label>
                    <label className="flex-1 cursor-pointer h-full relative z-10">
                      <input
                        type="radio"
                        name="is_active_add"
                        checked={addFormData.is_active === false}
                        onChange={() => handleAddFormChange('is_active', false)}
                        className="sr-only"
                      />
                      <motion.div
                        ref={inactiveStatusRef}
                        className="flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10"
                        animate={{
                          color: addFormData.is_active === false ? '#2563eb' : '#64748b'
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {t('common:inactive')}
                      </motion.div>
                    </label>
                  </div>
                </div>
                
                <div className="md:col-span-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700/50 dark:text-blue-200 text-sm">
                  <p className="font-semibold mb-1">{t('users.passwordNoteTitle')}</p>
                  <p>{t('users.passwordNoteBody')}</p>
                </div>
              </div>

              {/* Select Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('users.role')} *</label>
                  <div className="relative">
                    <select
                      value={addFormData.role}
                      onChange={(e) => handleAddFormChange('role', e.target.value)}
                      className="w-full appearance-none rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 pr-10 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    >
                      <option value="">{t('users.selectRole')}</option>
                      <option value="EMPLOYE">{t('common:roleEmployee')}</option>
                      <option value="MANAGER">{t('common:roleManager')}</option>
                      <option value="ADMIN">{t('common:roleAdmin')}</option>
                    </select>
                    <span className="material-symbols-outlined absolute right-3 top-3 text-slate-500 pointer-events-none">arrow_drop_down</span>
                  </div>
                </div>

                {/* Centre field - required for Employee */}
                {addFormData.role === 'EMPLOYE' && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-900 block">{t('users.centre')} *</label>
                    <div className="relative">
                      <select
                        value={addFormData.centre}
                        onChange={(e) => handleAddFormChange('centre', e.target.value)}
                        className="w-full appearance-none rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 pr-10 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        disabled={loadingCentres}
                      >
                        <option value="">{t('users.selectCentre')}</option>
                        {centres.map((c) => (
                          <option key={c.id} value={c.id.toString()}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <span className="material-symbols-outlined absolute right-3 top-3 text-slate-500 pointer-events-none">arrow_drop_down</span>
                    </div>
                  </div>
                )}

                {/* Team field - show for non-admin roles only (admins should not have a team) */}
                {addFormData.role !== 'ADMIN' && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-900 block">{t('users.team')}</label>
                    <div className="relative">
                      <select
                        value={addFormData.team}
                        onChange={(e) => handleAddFormChange('team', e.target.value)}
                        className="w-full appearance-none rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 pr-10 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        disabled={loadingTeams}
                      >
                        <option value="">{t('users.selectTeam')}</option>
                        {teams.map((team) => (
                          <option key={team.id} value={team.id.toString()}>
                            {team.name}
                          </option>
                        ))}
                      </select>
                      <span className="material-symbols-outlined absolute right-3 top-3 text-slate-500 pointer-events-none">arrow_drop_down</span>
                    </div>
                  </div>
                )}

                {/* Managed Teams - read-only for Managers */}
                {addFormData.role === 'MANAGER' && (
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-semibold text-slate-900 block">{t('users.managedTeams')}</label>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 min-h-[60px]">
                      <p className="text-sm text-slate-500 italic">{t('users.noTeamsAssigned')}</p>
                    </div>
                    <p className="text-xs text-slate-500">{t('users.managedTeamsHintAdd')}</p>
                  </div>
                )}
              </div>

              {/* Create Button */}
              <div className="pt-4 border-t border-slate-100">
                <button
                  onClick={handleAddUser}
                  disabled={adding}
                  className="w-full h-10 rounded-lg bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {adding ? t('common:saving') : t('users.createUser')}
                </button>
              </div>
                </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteModal.isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={handleDeleteCancel}
            />
            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-md w-full border border-slate-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                      <span className="material-symbols-outlined text-red-600 text-[24px]">
                        warning
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900 mb-2">
                        {t('users.deleteUser')}
                      </h3>
                      <p className="text-sm text-slate-600 mb-1">
                        {t('users.confirmDeleteNamed', { name: deleteModal.userName })}
                      </p>
                      <p className="text-sm text-red-600 font-medium">
                        {t('common:actionCannotBeUndone')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-3 mt-6">
                    <button
                      onClick={handleDeleteCancel}
                      className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                    >
                      {t('common:cancel')}
                    </button>
                    <button
                      onClick={handleDeleteConfirm}
                      className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      {t('common:delete')}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
        duration={toast.duration}
      />
    </div>
  );
};

export default UsersPage;

